import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Container,
  ResizeHandle,
  MapControls,
  ControlGroup,
  HoverBox,
  // FullscreenButton,
} from './Map.styles'
import { MapProps, Feature, Source } from './Map.types'
import { MapControl } from '../../atoms/MapControl'
import { TooltipProvider } from '../../atoms/Tooltip'
import { MapCornerControls } from './MapCornerControls'
// import { DrawingTools as ModularDrawingTools } from '../../molecules/DrawingTools'
import { MapDraw } from '../../molecules/MapDraw'
import { captureMapWithMarkers, downloadMapImage } from './Map.print'
// import { PrintControl as ModularPrintControl } from '../../molecules/PrintControl'
import { MAPBOX_TOKEN } from '../../../config'
import { transformTileRequest, addSourcesToMap, removeSources } from './Map.util'
import { MapDrawingProvider } from './MapDrawingContext'
import { useMapHandlers } from './Map.handlers'
import { useMapResize } from './hooks/core/useMapResize'
import { useMapBbox } from './hooks/core/useMapBbox'
import { useFlyToPosition } from './hooks/core/useFlyToPosition'
import { useUserLocation } from './hooks/core/useUserLocation'
// import { useFullscreen } from './hooks/core/useFullscreen'
import { useMapStatePersistence } from './hooks/useMapStatePersistence'
import { useParcelLayers } from './hooks/layers/useParcelLayers'
import { useBuildingLayers } from './hooks/layers/useBuildingLayers'
import { useBaseLayerVisibility } from './hooks/layers/useBaseLayerVisibility'
import { useDemographicsLayers } from './hooks/layers/useDemographicsLayers'
import { useEmploymentLayers } from './hooks/layers/useEmploymentLayers'
import { usePricingLayers } from './hooks/layers/usePricingLayers'
import { useNoiseLayers } from './hooks/layers/useNoiseLayers'
import { useYieldLayers } from './hooks/layers/useYieldLayers'
import { useRestrictionsLayers } from './hooks/layers/useRestrictionsLayers'
import { useHazardsLayers } from './hooks/layers/useHazardsLayers'
import { useZoningVectorLayers } from './hooks/layers/useZoningVectorLayers'
import { useHistoricalImageryLayer } from './hooks/layers/useHistoricalImageryLayer'
import { useEnergyLayers } from './hooks/layers/useEnergyLayers'
import { useSunlightLayers, removeShadeMapInstance } from './hooks/layers/useSunlightLayers'
import { useDynamicLayers } from './hooks/layers/useDynamicLayers'
import { useVectorTileDynamicLayers } from './hooks/layers/useVectorTileDynamicLayers'
import { useDistanceLimitLayer } from './hooks/layers/useDistanceLimitLayer'
import { MapControlsGroup } from './Map.controls'
import { useRouter } from '../../../hooks'
import { SWISS_TERRAIN_DEM_TILES, THREE_D_TERRAIN_SOURCE } from './terrain.constants'
import { POPETY_SATELLITE_MAP_STYLE_URL } from '../../../config/mapStylesConfig'
import { installSwissimageBasemap } from '../../../config/swissimageBasemap'

installSwissimageBasemap(mapboxgl)

// internal alias for the centralised 3D-terrain source id (see ./terrain.constants)
const threeDSource = THREE_D_TERRAIN_SOURCE
const DEFAULT_MAX_BBOX: mapboxgl.LngLatBoundsLike = [5.3559, 45.3179, 11.0921, 48.3084]

export const Map: React.FC<MapProps> = ({
  isPublic = false,
  width,
  height = 600,
  center = [8.2275, 46.8182], // Switzerland center coordinates
  zoom = 8,
  mobileZoom,
  data = [],
  markers = [],
  layers: _layers = [],
  legend: _legend = [],
  showControls = true,
  showBasemapToggle,
  showLegendControl = true,
  showLayersButton = true,
  showResizeHandle = true,
  showDimensionControl = true,
  showPointCloudControl = false,
  pointCloudActive = false,
  pointCloudLoading = false,
  onTogglePointCloud,
  enableClustering: _enableClustering = false,
  enableMeasurement = true,
  mapStyle = 'mapbox://styles/popety/cm7mg342r00u001s21uzzd25t',
  mapOptions = {},
  HoverComponent,
  ClickComponent,
  Controller,
  onFeatureClick,
  onFeatureHover,
  onMarkerClick,
  onMapMove,
  onDragStart,
  onLayerToggle,
  onLegendClick: _onLegendClick,
  onFullScreenChange,
  dimension: dimensionProp = '2D',
  bbox,
  maxBbox = DEFAULT_MAX_BBOX,
  padding = 100,
  bboxTransitionDuration = 1000,
  autoFitBounds = false,
  isDragPan = true,
  flyToPosition,
  flyToZoom,
  showFullScreenControl,
  fullScreen,
  defaultPitch = 0,
  showNavigation = true,
  className,
  style = {},
  children,
  LayersPanel: CustomLayersPanel,
  LegendPanel: CustomLegendPanel,
  PrintControl: CustomPrintControl,
  ExtraControl,
  _showBuiltInDrawingTools = true,
  showBuiltInPrintControl = true,
  setLayersPanelOpen = () => {},
  layersPanelOpen = false,
  setLegendPanelOpen: externalSetLegendPanelOpen,
  legendPanelOpen: externalLegendPanelOpen,
  onChangeMapStyle,
  mapStylePanelOpen,
  onMapSearch,
  activeLayerImage,
  enableDrawing = false,
  initialDrawingData = [],
  onDrawingComplete,
  onDrawingUpdate,
  onDrawingDelete,
  drawingToolsProps,
  drawingToolsLayout = 'fab',
  showFlyToPosition = true,
  propertyCoordinates,
  geoJson,
  distanceLimitValue,
  buildingGeoJson,
  buildingHeight,
  buildingDisplayHeight,
  buildingClassification,
  buildingProtectedRank,
  dynamicLayersData,
  onDynamicLayerClick,
  vectorTileDynamicLayersData,
  onVectorTileLayerClick,
  onVTTooltipChange,
  onVTPopoverChange,
  persistenceContext,
  baseLayerStates,
  onBaseLayerStatesRestore,
  onMapStyleRestore,
  ...otherMapOptions
}) => {
  const { t: tRestrictions } = useTranslation('restrictions')
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const popup = useRef<mapboxgl.Popup | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [styleLoadedCount, setStyleLoadedCount] = useState(0)

  const [currentStyle, setCurrentStyle] = useState<string>(mapStyle)
  const [restorationComplete, setRestorationComplete] = useState(false)
  // const [drawToolsOpen, setDrawToolsOpen] = useState(false)
  const [internalLegendPanelOpen, setInternalLegendPanelOpen] = useState(false)
  const legendPanelOpen = externalLegendPanelOpen ?? internalLegendPanelOpen
  const setLegendPanelOpen = externalSetLegendPanelOpen ?? setInternalLegendPanelOpen
  // const [activeDrawTool, setActiveDrawTool] = useState<string | null>(null)
  const [dimension, setDimension] = useState(dimensionProp)
  // Mirror dimension into a ref so the long-lived `style.load` listener (registered once at map
  // init) can read the current 2D/3D state without being re-bound on every dimension change.
  const dimensionRef = useRef(dimension)
  useEffect(() => {
    dimensionRef.current = dimension
  }, [dimension])
  const [isDrawingActive, setIsDrawingActive] = useState(false)
  const [hoverFeature, setHoverFeature] = useState<Feature>()
  const [clickFeature, setClickFeature] = useState<Feature>()
  const [isFullScreen, setIsFullScreen] = useState(fullScreen || false)

  // Internal satellite toggle when onChangeMapStyle is not provided
  const SATELLITE_STYLE = POPETY_SATELLITE_MAP_STYLE_URL
  const [isSatelliteActive, setIsSatelliteActive] = useState(false)
  const previousStyleRef = useRef(mapStyle)

  const internalChangeMapStyle = useCallback(() => {
    if (!map.current) return
    if (isSatelliteActive) {
      map.current.setStyle(previousStyleRef.current)
      setIsSatelliteActive(false)
    } else {
      previousStyleRef.current = map.current.getStyle()?.sprite ? currentStyle : mapStyle
      map.current.setStyle(SATELLITE_STYLE)
      setIsSatelliteActive(true)
    }
  }, [isSatelliteActive, currentStyle, mapStyle])

  const effectiveOnChangeMapStyle = onChangeMapStyle || internalChangeMapStyle
  const effectiveMapStylePanelOpen = mapStylePanelOpen ?? isSatelliteActive

  // Track if a flyTo is in progress to prevent bbox from interrupting
  // Using ref instead of state to avoid re-renders that could trigger other effects
  const isFlyingToRef = useRef(false)

  // Track if initial bbox fit has been done to prevent repeated fits
  const hasInitiallyFit = useRef(false)

  const [mapBearing, setMapBearing] = useState(0)
  const { updateQuery } = useRouter()

  // Keep track of existing sources for cleanup
  const existingSources = useRef<Source[]>([])

  // Fullscreen functionality
  // const { isFullscreen, toggleFullscreen } = useFullscreen(mapContainer as any)

  // Map state persistence
  // Note: We don't persist center/zoom to allow each analysis to show its specific location
  const { savedState } = useMapStatePersistence({
    map: map.current,
    context: persistenceContext || '',
    currentState: {
      bearing: map.current?.getBearing(),
      pitch: map.current?.getPitch(),
      dimension,
      mapStyle: currentStyle,
      baseLayerStates: baseLayerStates,
      panelStates: {
        layers: layersPanelOpen,
        legend: legendPanelOpen,
        mapStyle: mapStylePanelOpen,
      },
    },
  })

  // Handle drawing tool changes
  const handleDrawingToolChange = useCallback((tool: string) => {
    setIsDrawingActive(tool !== 'none')
  }, [])

  // Initialize Mapbox
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    // Validate center coordinates
    const validatedCenter =
      center &&
      Array.isArray(center) &&
      center.length === 2 &&
      !isNaN(center[0]) &&
      !isNaN(center[1])
        ? center
        : [8.2275, 46.8182] // Switzerland center as fallback

    // Use saved state for initial view if available, otherwise use props
    // Note: We don't restore center/zoom from savedState to allow analysis-specific coordinates
    const initialCenter = validatedCenter
    // Use mobileZoom on mobile viewports (< 640px) if provided
    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 640
    const initialZoom = isMobileViewport && mobileZoom !== undefined ? mobileZoom : zoom
    const initialPitch = savedState?.pitch ?? defaultPitch
    const initialBearing = savedState?.bearing ?? 0
    const initialStyle = savedState?.mapStyle || mapStyle

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle,
      center: initialCenter as [number, number],
      zoom: initialZoom,
      pitch: initialPitch,
      bearing: initialBearing,
      transformRequest: transformTileRequest,
      preserveDrawingBuffer: true, // Required for canvas export
      ...mapOptions,
      ...otherMapOptions,
      // Public shares keep their allowed area from the first frame, including after style changes.
      ...(isPublic ? { maxBounds: maxBbox, projection: { name: 'mercator' as const } } : {}),
    })

    popup.current = new mapboxgl.Popup({
      className: 'map-popup-no-padding',
      closeButton: false,
      closeOnClick: false,
    })

    // Add error handler for style evaluation errors
    map.current.on('error', (e) => {
      // Suppress non-critical style evaluation errors
      if (e.error && e.error.message && e.error.message.includes('Failed to evaluate expression')) {
        // Prevent the error from propagating if preventDefault exists
        if (typeof (e as any).preventDefault === 'function') {
          ;(e as any).preventDefault()
        }
      }
    })

    map.current.on('load', () => {
      setMapLoaded(true)
      setStyleLoadedCount((prev) => prev + 1)
      // Configure dragPan with optimized inertia settings for smoother panning
      // These settings provide better UX when interrupting inertia with a new drag
      if (map.current) {
        map.current.dragPan.enable({
          linearity: 0.15,
          maxSpeed: 1400,
          deceleration: 2500,
        })
      }

      // Add initial data sources
      if (data.length > 0) {
        addSourcesToMap(data, map.current!)
        existingSources.current = data
      }
      // Markers are handled by useEffect below
    })

    // Re-assert our swissALTI3D terrain while in 3D. Two things wipe it: (a) a basemap swap
    // (setStyle) rebuilds the style and drops custom sources + terrain while dimension stays '3D';
    // (b) Mapbox **Standard** styles (e.g. standard-satellite) install their OWN terrain — an
    // exaggeration that interpolates to 0 at high zoom — asynchronously AFTER style.load, flattening
    // the ground. Either way a terrain-anchored overlay (the swissSURFACE3D point cloud, placed at
    // absolute altitude in 3D) floats off-screen. Guarded so it's a no-op once the terrain is ours
    // (no flicker, no loop). Pitch is left alone: a top-down view in 3D stays top-down.
    const ensure3DTerrain = () => {
      const m = map.current
      if (!m || dimensionRef.current !== '3D') return
      if (!m.getSource(threeDSource)) {
        m.addSource(threeDSource, {
          type: 'raster-dem',
          tiles: [SWISS_TERRAIN_DEM_TILES],
          encoding: 'mapbox',
          tileSize: 512,
          maxzoom: 17,
        })
      }
      const terrain = m.getTerrain() as {
        source?: string
        exaggeration?: number
      } | null
      const isOurTerrain =
        !!terrain && terrain.source === threeDSource && terrain.exaggeration === 1
      if (!isOurTerrain) m.setTerrain({ source: threeDSource, exaggeration: 1 })
    }

    // Listen for style changes
    map.current.on('style.load', () => {
      setStyleLoadedCount((prev) => prev + 1)
      ensure3DTerrain() // immediate (may be overridden by a Standard style's late config…)
    })
    // …so also re-assert on `idle`, which fires AFTER the new style fully settles. This is the
    // chokepoint that survives a basemap swap in 3D (and a `?dimension=3D` deep link, where terrain
    // was previously only applied on a 3D button click — see lidar-point-cloud gotchas #6).
    map.current.on('idle', ensure3DTerrain)

    map.current.on('moveend', () => {
      if (onMapMove && map.current) {
        const center = map.current.getCenter()
        const zoom = map.current.getZoom()
        onMapMove([center.lng, center.lat], zoom)
      }
    })

    // Track bearing changes for compass
    map.current.on('rotate', () => {
      if (map.current) {
        const bearing = map.current.getBearing()
        setMapBearing(bearing)
      }
    })

    return () => {
      if (map.current) {
        // Clear terrain before destroying the map to prevent _updateTerrain
        // from crashing when sources are removed during map.remove()
        try {
          map.current.setTerrain(null)
        } catch {
          // ignore
        }
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  // Handle drag start event separately to ensure proper callback binding
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const handleDragStart = () => {
      if (onDragStart) {
        onDragStart()
      }
    }

    map.current.on('dragstart', handleDragStart)

    return () => {
      if (map.current) {
        map.current.off('dragstart', handleDragStart)
      }
    }
  }, [mapLoaded, onDragStart])

  // Restore saved state for dimension, style, panel states, and base layer states
  // This effect runs ONCE on mount to restore persisted state, then never again
  useEffect(() => {
    if (!savedState || !mapLoaded || restorationComplete) return

    let restored = false

    // Restore dimension mode
    if (savedState.dimension && savedState.dimension !== dimension) {
      setDimension(savedState.dimension)
      restored = true
    }

    // Restore map style if different
    if (savedState.mapStyle && savedState.mapStyle !== currentStyle) {
      setCurrentStyle(savedState.mapStyle)
      restored = true
      // Also notify parent component to update its map style state
      if (onMapStyleRestore) {
        onMapStyleRestore(savedState.mapStyle)
      }
    }

    // Restore panel states
    if (savedState.panelStates) {
      if (savedState.panelStates.layers !== undefined) {
        setLayersPanelOpen(savedState.panelStates.layers)
        restored = true
      }
      if (savedState.panelStates.legend !== undefined) {
        setLegendPanelOpen(savedState.panelStates.legend)
        restored = true
      }
    }

    // Restore base layer states (for base layers like streets, labels, terrain, etc.)
    if (savedState.baseLayerStates && onBaseLayerStatesRestore) {
      onBaseLayerStatesRestore(savedState.baseLayerStates)
      restored = true
    }

    // Mark restoration as complete after first run
    // This prevents the effect from re-running when currentStyle or dimension changes
    if (restored) {
      setRestorationComplete(true)
    }
  }, [
    savedState,
    mapLoaded,
    restorationComplete,
    // dimension,  // REMOVED: Prevents re-triggering on user changes
    // currentStyle,  // REMOVED: This was causing the race condition!
    onBaseLayerStatesRestore,
    onMapStyleRestore,
  ])

  // Observe container size changes and resize map
  useEffect(() => {
    if (!mapContainer.current || !map.current) return

    const resizeObserver = new ResizeObserver(() => {
      // Debounce the resize to avoid too many calls
      if (map.current) {
        // Small timeout to ensure the map container has finished resizing
        setTimeout(() => {
          if (map.current) {
            map.current.resize()
          }
        }, 150)
      }
    })

    resizeObserver.observe(mapContainer.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [mapLoaded])

  // Update data sources
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // A basemap swap (setStyle) wipes custom sources and layers without changing
    // `data`. Re-run on every style reload (styleLoadedCount) to re-add them.
    // addSource throws until the style has finished loading, so defer to idle.
    // With no sources to add or remove (VectorTileMap passes `data={[]}`) there is
    // nothing to wait for: deferring would bump styleLoadedCount on every idle and
    // re-init the vector-tile layers in a loop.
    const hasSourceWork = data.length > 0 || existingSources.current.length > 0
    if (hasSourceWork && !map.current.isStyleLoaded()) {
      map.current.once('idle', () => setStyleLoadedCount((prev) => prev + 1))
      return
    }

    const existingSourceIds = existingSources.current.map((s) => s.id)
    const newSourceIds = data.map((s) => s.id)

    const isSameSourceSet =
      existingSourceIds.length === newSourceIds.length &&
      existingSourceIds.every((id, idx) => id === newSourceIds[idx])
    const allSourcesPresent = data.every((source) => !!map.current?.getSource(source.id))

    if (isSameSourceSet && existingSources.current.length > 0 && allSourcesPresent) {
      // Same sources — update GeoJSON data in place (no tile jump)
      data.forEach((source) => {
        if (source.type === 'geojson' && source.data) {
          const mapSource = map.current?.getSource(source.id) as mapboxgl.GeoJSONSource

          if (mapSource && typeof mapSource.setData === 'function') {
            mapSource.setData(source.data as any)
          }
        }
      })
    } else {
      // Source set changed — full removal and re-addition
      removeSources(existingSources.current, map.current)
      addSourcesToMap(data, map.current)
    }

    existingSources.current = data

    // Re-enable event handlers for new layers
    enableMapEvents()

    return () => {
      disableMapEvents()
    }
  }, [data, mapLoaded, styleLoadedCount])

  // Handle fullscreen change
  useEffect(() => {
    setIsFullScreen(fullScreen || false)
  }, [fullScreen])

  // Map event handlers
  const {
    enableMapEvents,
    disableMapEvents,
    addMarkers: handleAddMarkers,
    removeMarkers: handleRemoveMarkers,
  } = useMapHandlers({
    map,
    popup,
    mapLoaded,
    data,
    onFeatureClick,
    onFeatureHover,
    onMarkerClick,
    setHoverFeature,
    setClickFeature,
  })

  // Handle map resize
  useMapResize({ map, mapLoaded, width, height })

  // Handle bbox fitting
  useMapBbox({
    map,
    mapLoaded,
    bbox,
    maxBbox,
    padding,
    autoFitBounds,
    isDragPan,
    bboxTransitionDuration,
    isFlyingToRef,
    hasInitiallyFit,
    isPublic,
  })

  // Handle fly to position
  useFlyToPosition({
    map,
    mapLoaded,
    flyToPosition,
    flyToZoom,
    isFlyingToRef,
    isPublic,
    maxBbox,
  } as any)

  // Handle user location (or property location if propertyCoordinates is provided for analysis maps)
  const { handleFlyToUserLocation } = useUserLocation({
    map,
    mapLoaded,
    propertyCoordinates,
  })

  // Handle parcel layers
  useParcelLayers({
    map,
    mapLoaded,
    geoJson,
    layers: _layers,
    styleLoadedCount,
  })

  // Handle distance limit buffer layer
  useDistanceLimitLayer({
    map,
    mapLoaded,
    geoJson,
    distanceLimitValue,
    layers: _layers,
    styleLoadedCount,
  })

  // Handle building layers
  useBuildingLayers({
    map,
    mapLoaded,
    buildingGeoJson,
    buildingHeight,
    buildingDisplayHeight,
    buildingClassification,
    buildingProtectedRank,
    layers: _layers,
    styleLoadedCount,
  })

  // Handle base layer visibility
  useBaseLayerVisibility({
    map,
    mapLoaded,
    layers: _layers,
    currentStyle,
    mapStyle,
    styleLoadedCount,
  })

  // Handle demographics layers
  useDemographicsLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle employment layers
  useEmploymentLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle pricing layers
  usePricingLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle noise layers
  useNoiseLayers({
    isPublic,
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle yield hexagon layers
  useYieldLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle restrictions layers
  useRestrictionsLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
    tRestrictions,
  })

  // Handle hazard zone layers
  useHazardsLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle energy layers (solar potential roof polygons)
  useEnergyLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle sunlight layers (shadow simulation and sunlight hours)
  useSunlightLayers({
    map,
    mapLoaded,
    popup,
    styleLoadedCount,
    otherMapOptions: {
      ...otherMapOptions,
      propertyCoordinates,
      buildingGeoJson,
    },
  })

  // Handle zoning GeoJSON layers (using batch API data)
  useZoningVectorLayers(map, mapLoaded, styleLoadedCount, otherMapOptions)

  // Handle historical imagery layer (raster tiles)
  useHistoricalImageryLayer({
    map,
    mapLoaded,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle dynamic layers
  useDynamicLayers({
    map,
    mapLoaded,
    popup,
    dynamicLayersData,
    styleLoadedCount,
    onDynamicLayerClick,
  } as any)

  // Handle vector tile dynamic layers (background tile rendering)
  useVectorTileDynamicLayers({
    map,
    mapLoaded,
    styleLoadedCount,
    popup,
    vectorTileDynamicLayersData,
    onVectorTileLayerClick,
    onVTTooltipChange,
    onVTPopoverChange,
  })

  // Add markers effect
  useEffect(() => {
    if (mapLoaded) {
      handleAddMarkers(markers)
    }
    // Cleanup markers on unmount
    return () => {
      handleRemoveMarkers()
    }
  }, [markers, mapLoaded, handleAddMarkers, handleRemoveMarkers])

  // Sync currentStyle with mapStyle prop changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    if (mapStyle !== currentStyle) {
      setCurrentStyle(mapStyle)
      // Reset internal satellite state when style changes externally
      setIsSatelliteActive(mapStyle.includes('satellite'))

      // Tear down ShadeMap BEFORE swapping styles. ShadeMap.remove() calls
      // map.removeLayer(), which throws once the swap has begun (this.style
      // becomes undefined), leaving a zombie WebGL render loop that white-pages
      // the sunlight section when switching to satellite. Removing here, while
      // the current style is still valid, lets it tear down cleanly; the
      // sunlight effect re-adds it after the new style finishes loading.
      removeShadeMapInstance()

      // Remove terrain before style swap to prevent crash during source cleanup.
      // Mapbox internally calls _updateTerrain when removing sources, and if
      // terrain references a source being removed, it throws.
      if (map.current.getSource(threeDSource)) {
        try {
          map.current.setTerrain(null)
        } catch {
          // ignore if terrain was already cleared
        }
      }

      // Actually update the map style
      map.current.setStyle(mapStyle)

      // Listen for when the new style finishes loading.
      // `style.load` can fire a tick BEFORE the style reports fully loaded, so
      // bumping styleLoadedCount here would make all dependent layer hooks call
      // addSource/addLayer too early — Mapbox then throws "Style is not done
      // loading", an uncaught error that white-pages the section. Only signal
      // re-add once isStyleLoaded() is true, deferring to 'idle' otherwise.
      const handleStyleLoad = () => {
        if (!map.current) return
        if (map.current.isStyleLoaded()) {
          setStyleLoadedCount((prev) => prev + 1)
        } else {
          map.current.once('idle', () => {
            setStyleLoadedCount((prev) => prev + 1)
          })
        }
      }

      // Add one-time listener for style.load event
      map.current.once('style.load', handleStyleLoad)
    }
  }, [mapStyle, mapLoaded])

  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    if (map.current) map.current.zoomIn()
  }, [])

  // Placeholder functions for event handlers (will be replaced by actual handlers in the useEffect)
  // const handleDynamicLayerClick = useCallback(() => {}, [])
  // const handleClusterClick = useCallback(() => {}, [])

  const handleZoomOut = useCallback(() => {
    if (map.current) map.current.zoomOut()
  }, [])

  // Handle compass click to reset to north
  const handleCompassClick = useCallback(() => {
    if (map.current) {
      map.current.easeTo({
        bearing: 0,
        duration: 1000,
      })
    }
  }, [])

  const applyDimension = useCallback(
    (next: '2D' | '3D') => {
      if (!map.current) return
      const hasTerrain = Boolean(map.current.getTerrain())
      if (next === '3D' && hasTerrain) {
        setDimension('3D')
        return
      }
      if (next === '2D' && !hasTerrain) {
        setDimension('2D')
        return
      }

      setDimension(next)
      if (next === '3D') {
        if (!map.current.getSource(threeDSource)) {
          map.current.addSource(threeDSource, {
            type: 'raster-dem',
            tiles: [SWISS_TERRAIN_DEM_TILES],
            encoding: 'mapbox',
            tileSize: 512,
            maxzoom: 17,
          })
        }
        map.current.setTerrain({ source: threeDSource, exaggeration: 1 })
        map.current.setPitch(60)
        map.current.setBearing(-17.6)
      } else {
        map.current.setTerrain()
        if (map.current.getSource(threeDSource)) {
          map.current.removeSource(threeDSource)
        }
        map.current.setPitch(defaultPitch)
        map.current.setBearing(0)
      }
    },
    [defaultPitch]
  )

  // Handle 3D view toggle from the built-in map button.
  const handleToggle3D = useCallback(() => {
    const newDimension = dimension === '2D' ? '3D' : '2D'
    applyDimension(newDimension)
    updateQuery({ dimension: newDimension })
  }, [dimension, applyDimension])

  // Explore drives 2D/3D from the view control, which updates the dimension prop.
  const dimensionPropRef = useRef(dimensionProp)
  useEffect(() => {
    if (!map.current || !mapLoaded) return
    const propChanged = dimensionPropRef.current !== dimensionProp
    dimensionPropRef.current = dimensionProp
    if (!propChanged) {
      if (dimensionProp === '3D' && !map.current.getTerrain()) {
        applyDimension('3D')
      }
      return
    }
    applyDimension(dimensionProp)
  }, [dimensionProp, mapLoaded, applyDimension])

  // Handle draw tool selection
  // const handleDrawTool = useCallback(
  //   (toolId: string) => {
  //     setActiveDrawTool(activeDrawTool === toolId ? null : toolId)
  //     setDrawToolsOpen(false)

  //     // Drawing functionality would be implemented here
  //     // You could add mapbox-gl-draw or custom drawing logic
  //   },
  //   [activeDrawTool]
  // )

  // // Handle delete drawings
  // const handleDeleteDrawings = useCallback(() => {
  //   setActiveDrawTool(null)
  //   setDrawToolsOpen(false)

  //   // Deletion functionality would be implemented here
  // }, [])

  // // Handle thematic layer change
  // const handleThematicLayerChange = useCallback(
  //   (layerId: string, enabled: boolean) => {
  //     setSelectedThematicLayer(enabled ? layerId : '')
  //     setLegendPanelOpen(enabled && showLegend)

  //     // Thematic layer visibility would be implemented here
  //   },
  //   [showLegend]
  // )

  const handlePrintMap = useCallback(async () => {
    if (!map.current || !mapContainer.current) return
    try {
      const imageUrl = await captureMapWithMarkers({
        map: map.current,
        mapContainer: mapContainer.current,
      })
      if (imageUrl) await downloadMapImage(imageUrl)
    } catch (error) {
      console.error('Failed to capture map:', error)
    }
  }, [])

  // Handle fullscreen toggle
  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreen((prev) => !prev)
    onFullScreenChange?.()
  }, [])

  // Prepare common props for child components
  const commonProps = {
    map: map.current ?? undefined,
    popup: popup.current ?? undefined,
    mapboxgl,
    clickFeature,
    setClickFeature,
    loaded: mapLoaded,
    styleLoadedCount,
    dimension,
    setDimension,
  }

  // Clone children with props
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, commonProps as any)
    }
    return child
  })

  const minHeight = isFullScreen ? 'calc(100vh - 160px)' : '400px'

  return (
    <MapDrawingProvider value={isDrawingActive}>
      <Container
        className={className}
        style={{
          ...(style as any),
          ...(isFullScreen && {
            minHeight,
            overflow: 'hidden',
          }),
        }}
        $width={width}
        $height={height}
      >
        {/* Decorative only — no drag handler. Hide when a parent panel resizes the split. */}
        {showResizeHandle && <ResizeHandle data-testid="map-resize-handle" />}

        {/* Map Controls */}
        {showControls && (
          <MapControls data-map-controls>
            <ControlGroup>
              <MapControlsGroup
                showControls={showControls}
                layersPanelOpen={layersPanelOpen}
                setLayersPanelOpen={setLayersPanelOpen}
                legendPanelOpen={legendPanelOpen}
                setLegendPanelOpen={setLegendPanelOpen}
                showLegendControl={showLegendControl}
                showLayersButton={showLayersButton}
                showDimensionControl={showDimensionControl}
                dimension={dimension}
                onToggle3D={handleToggle3D}
                showPointCloudControl={showPointCloudControl}
                pointCloudActive={pointCloudActive}
                pointCloudLoading={pointCloudLoading}
                onTogglePointCloud={onTogglePointCloud}
                showBuiltInPrintControl={
                  drawingToolsLayout === 'toolbar' ? false : showBuiltInPrintControl
                }
                map={map}
                mapContainer={mapContainer}
                CustomPrintControl={CustomPrintControl}
                ExtraControl={ExtraControl}
                activeLayerImage={activeLayerImage}
                onFlyToUserLocation={handleFlyToUserLocation}
              />
            </ControlGroup>
          </MapControls>
        )}

        <TooltipProvider>
          <MapCornerControls
            showNavigation={showNavigation}
            showLocate={showFlyToPosition}
            showBasemap={showBasemapToggle ?? showControls}
            satelliteActive={effectiveMapStylePanelOpen}
            flyToProperty={Boolean(propertyCoordinates)}
            bearing={mapBearing}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetNorth={handleCompassClick}
            onLocate={handleFlyToUserLocation}
            onToggleBasemap={effectiveOnChangeMapStyle}
          />

          {/* Fullscreen Control */}
          {showFullScreenControl && (
            <MapControl
              icon={
                <svg viewBox="0 0 24 24">
                  <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
                </svg>
              }
              title={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
              onClick={handleToggleFullScreen}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 10,
              }}
            />
          )}

          {/* Drawing Tools */}
          {enableMeasurement && enableDrawing && (
            <MapDraw
              map={map.current}
              enabled={enableDrawing}
              onDrawingComplete={onDrawingComplete}
              onDrawingUpdate={onDrawingUpdate}
              onDrawingDelete={onDrawingDelete}
              onToolChange={handleDrawingToolChange}
              initialData={initialDrawingData}
              drawingToolsProps={
                drawingToolsLayout === 'toolbar'
                  ? {
                      ...drawingToolsProps,
                      layout: 'toolbar',
                      onPrint: handlePrintMap,
                    }
                  : drawingToolsProps
              }
            />
          )}
        </TooltipProvider>

        {/* Hover Component */}
        {hoverFeature && HoverComponent && (
          <HoverBox className="HoverBox" left={hoverFeature.x} top={hoverFeature.y}>
            <HoverComponent {...hoverFeature} />
          </HoverBox>
        )}

        {/* Click Component */}
        {ClickComponent && (
          <ClickComponent {...commonProps} {...clickFeature} setClickFeature={setClickFeature} />
        )}

        {/* Controller Component */}
        {Controller && <Controller {...commonProps} />}

        {/* Map Container */}
        <div
          ref={mapContainer}
          className="map-container"
          style={{ width: '100%', height: '100%' }}
        />

        {/* Children with map context */}
        {childrenWithProps}
      </Container>
    </MapDrawingProvider>
  )
}
